import { Box } from "@mui/material";
import { useState } from "react";
import {
  Option,
  FormFieldsConfig,
  Value,
  ConstraintMessage,
  ParametricConstraintMessage,
} from "./FormEntities";

import FormUtils from "./FormUtils";
import validateField from "./FormValidator";
import { useTranslation } from "react-i18next";
import { capitalizeWords } from "pages/Utils/Formats";

export default function useForm(
  formConfig: FormFieldsConfig,
  realTimeValidation: boolean = true
) {
  const { t } = useTranslation();

  const formatters = FormUtils.getFormatters(formConfig);
  const constraints = FormUtils.getConstraints(formConfig);
  const [values, setValues] = useState(FormUtils.getInitValues(formConfig));
  const [options, setOptions] = useState(FormUtils.getOptions(formConfig));
  const [errors, setErrors] = useState<Record<string, Array<string>>>({});

  function handleInputChange(e: any) {
    const { name, value } = e.target;
    let formattedValue = value;

    if (formatters[name]){
      if (formatters[name] === 'capitalizeWords'){
        formattedValue = capitalizeWords(value);
      }
      // if (typeof formatters[name] === "function"){
      //   formattedValue = formatters[name](value);
      // }
    }

    setValues((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (realTimeValidation) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, formattedValue, values, constraints),
      }));
    }
  }

  function updateValues(valueStatement: Record<string, Value>) {
    setValues((prev) => ({
      ...prev,
      ...valueStatement,
    }));
  }

  function updateOptions(optionStatement: Record<string, Array<Option>>) {
    setOptions((prev) => ({
      ...prev,
      ...optionStatement,
    }));
  }

  function isValidForm() {
    let formErrors: any = {};
    for (const [name, value] of Object.entries(values)) {
      const fieldErrors = validateField(name, value, values, constraints);
      if (Object.keys(fieldErrors).length !== 0) {
        formErrors[name] = fieldErrors;
      }
    }
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  }

  function formatErrorObject(fieldErrors: Array<ConstraintMessage>) {
    let errorObject: OutputError = {};
    if (!fieldErrors || fieldErrors.length === 0) {
      errorObject = {
        error: false,
        helperText: "",
      };
    } else {
      let formattedMessages: Array<ParametricConstraintMessage> =
        fieldErrors.map((errorMessage, i) => {
          let errorName, errorParameters;
          if (typeof errorMessage === "string") {
            errorName = errorMessage;
            errorParameters = {};
          } else if (typeof errorMessage === "function") {
            errorName = errorMessage(values).name;
            errorParameters = errorMessage(values).params;
          } else {
            errorName = errorMessage.name;
            errorParameters = errorMessage.params;
          }
          return {
            name: errorName,
            params: errorParameters,
          };
        });
      errorObject = {
        error: true,
        helperText: (
          <>
            {formattedMessages.map((errorMessage, i) => (
              <span key={i}>
                {
                  <span>
                    {t(`FORM.ERRORS.${errorMessage.name}`, errorMessage.params)}
                  </span>
                }
                <br />
              </span>
            ))}
          </>
        ),
      };
    }
    return errorObject;
  }

  return {
    errors,
    setErrors,
    values,
    setValues,
    options,
    setOptions,

    updateValues,
    updateOptions,

    handleInputChange,
    isValidForm,
    formatErrorObject,
    Form,
  };
}

function Form(props: any) {
  const { children, onSubmit, ...other } = props;
  function handleSubmit(e: any) {
    e.preventDefault();
    onSubmit(e);
  }
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate {...other}>
      {children}
    </Box>
  );
}

type OutputError =
  | {
      error: boolean;
      helperText: any;
    }
  | {};
